import React, { useCallback, useState } from 'react';
import { AppButton, Box, FormProvider, getValidationResolver, Inscription, Preloader, useForm } from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { UNAUTHORIZED_STATUS_CODE } from '@/api/api';
import { VALIDATORS } from '@/common';
import { AUTH_ERROR_KEY } from '@/common/constants';
import { ModalWrapper } from '@/components/wrappers';
import { ChangePasswordField, ChangePasswordForm, ChangePasswordStep } from '@/modules/user/components/ChangePasswordModal/changePassword.types';
import useChangePasswordQuery from '@/modules/user/queries/useChangePassword.query';
import theme from '@/styles/theme';

import ChangePasswordErrorModal from './ChangePasswordErrorModal';
import EnterNewPasswordFields from './EnterNewPasswordFields';

interface Props {
  onSetVisible: (value: boolean) => void;
  setStep: (step: ChangePasswordStep) => void;
}

const EnterNewPasswordModal: React.FC<Props> = ({ onSetVisible, setStep }) => {
  const { changePassword, isLoading } = useChangePasswordQuery();

  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);

  const methods = useForm<ChangePasswordForm>({
    resolver: getValidationResolver({
      [ChangePasswordField.existingPassword]: yup.string().required(AUTH_ERROR_KEY.PASSWORD_IS_REQUIRED),
      [ChangePasswordField.newPassword]: VALIDATORS.PASSWORD,
      [ChangePasswordField.confirmNewPassword]: yup.string().required(AUTH_ERROR_KEY.PASSWORD_IS_REQUIRED)
        .oneOf([yup.ref(ChangePasswordField.newPassword)], AUTH_ERROR_KEY.PASSWORD_DO_NOT_MATCH),
    })
  });

  const handleCancel = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const handleSubmit = useCallback(async (passwords: ChangePasswordForm) => {
    try {
      await changePassword({
        password: passwords[ChangePasswordField.existingPassword],
        newPassword: passwords[ChangePasswordField.newPassword],
      });
      setStep(ChangePasswordStep.SUCCESSFULLY_CHANGED);
    } catch (error) {
      const errorCode = _.get(error, 'response.status', 0) as number;
      const errorMessage = _.get(error, 'response.data.elevate_error_message', '') as string;

      const isCurrentPasswordInvalid = errorCode === UNAUTHORIZED_STATUS_CODE;
      const isNewPasswordTheSame = errorMessage?.includes(AUTH_ERROR_KEY.PASSWORD_MUST_BE_DIFFERENT);

      if (isCurrentPasswordInvalid) {
        methods.setError(ChangePasswordField.existingPassword, { message: AUTH_ERROR_KEY.PASSWORD_INVALID });
        setIsErrorModalVisible(false);
        return;
      }

      if (isNewPasswordTheSame) {
        methods.setError(ChangePasswordField.newPassword, { message: AUTH_ERROR_KEY.PASSWORD_MUST_BE_DIFFERENT });
        methods.setError(ChangePasswordField.confirmNewPassword, { message: AUTH_ERROR_KEY.PASSWORD_MUST_BE_DIFFERENT });
        setIsErrorModalVisible(false);
        return;
      }

      setIsErrorModalVisible(true);
      throw new Error();
    }
  }, [changePassword, setStep, methods]);

  if (isErrorModalVisible) {
    return (
      <ChangePasswordErrorModal
        onSetVisible={setIsErrorModalVisible}
        onSubmit={methods.handleSubmit(handleSubmit)}
      />
    );
  }

  return (
    <ModalWrapper
      testId="change-password"
      visible
      onSetVisible={onSetVisible}
      title="Change Password"
      titleBoxStyle={{ paddingBottom: theme.spacings.spacing8 }}
    >
      <Inscription size={theme.spacings.m} textAlign="center">
        Please enter an existing password and choose your new password.
      </Inscription>

      <Box
        margin={{ top: 'spacing24' }}
        background="module"
        pad="spacing24"
        round="moduleRound"
      >
        <FormProvider {...methods}>
          <form>
            <EnterNewPasswordFields editMode />
          </form>
        </FormProvider>
      </Box>
  
      <Box
        margin={{ top: 'spacing24' }}
        direction="row"
        justify="end"
      >
        <Box width={{ min: '140px' }}>
          <AppButton
            width="100%"
            buttonType="secondary"
            onClick={handleCancel}
          >
            Cancel
          </AppButton>
        </Box>
        <Box
          margin={{ left: 'spacing12' }}
          width={{ min: '140px' }}
          onClick={methods.handleSubmit(handleSubmit)}
        >
          <AppButton width="100%" disabled={isLoading}>
            {isLoading ? <Preloader color="white" /> : 'Confirm'}
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default EnterNewPasswordModal;
